<script lang="ts">
    import {
        ActiveTopicStore,
        TopicAccessLevel,
    } from "$/stores/extension/ActiveTopicStore";

    import TagItem from "./TagItem.svelte";
    import { removeSuggestionList } from "../../utils";
    import { clickOutside } from "$/utils/clickOutside.js";
    import { TenantStore } from "$/stores/TenantStore";
    import { v4 as uuidv4 } from "uuid";
    import { isElementOverflown } from "$/utils/Utils";
    import { AppStore, AppType } from "$/stores/AppStore";

    export let props;
    export let userRole = TopicAccessLevel.OWNER;
    export let isComment = true;
    export let inverseList = false;
    export let snippetTag = false;

    let isExtensionOrHighlighter =
        $AppStore.type === AppType.EXTENSION ||
        $AppStore.type === AppType.HIGHLIGHTER;

    const SCROLL_HEIGHT = isComment ? 144 : 96;

    let element;
    let editingTagId = null;
    let selectedIndex = 0;
    let tagItemRefs = [];
    $: menus = props.items.sort((a, b) =>
        inverseList || (snippetTag && !isExtensionOrHighlighter)
            ? b.tag.length - a.tag.length
            : a.tag.length - b.tag.length,
    );

    // set new tag button visibility if user typed something and there is no item with the same tag in lowercase
    $: isNewTagButtonVisible =
        props.query &&
        !menus.filter((item) => item.tag === props.query.toLowerCase()).length;

    $: menus, menus && calculateSelectedIndex();

    export const onKeyDown = ({ event }: { event: KeyboardEvent }) => {
        if (event.key === "ArrowUp" || event.key === "ArrowDown") {
            const isArrowUp = event.key === "ArrowUp";
            const reverse = isComment || isExtensionOrHighlighter;
            const direction = isArrowUp !== reverse ? "up" : "down";

            handleArrowKey(direction);
            return true;
        }

        if (event.key === "Enter" || event.key === "Tab") {
            enterHandler();
            return true;
        }

        if (event.key === "Escape") {
            removeSuggestionList();
            return true;
        }

        return false;
    };

    const calculateSelectedIndex = () => {
        selectedIndex = isNewTagButtonVisible
            ? -1
            : inverseList || (snippetTag && !isExtensionOrHighlighter)
              ? menus.length - 1
              : 0;
        scrollElementToPosition(inverseList || snippetTag ? 0 : 1000000);
    };

    const handleArrowKey = (direction: "up" | "down") => {
        const isUp = direction === "up";
        const isAtBoundary =
            (!isUp && selectedIndex === 0) ||
            (isUp && selectedIndex === menus.length - 1);

        if (isNewTagButtonVisible && isAtBoundary) {
            selectedIndex = -1;
        } else if (selectedIndex === -1) {
            selectedIndex = !isUp ? menus.length - 1 : 0;
        } else {
            selectedIndex =
                (selectedIndex + (isUp ? 1 : -1) + menus.length) % menus.length;
        }
        scrollSuggestionMenu(selectedIndex);
    };

    const enterHandler = () => {
        onSelect(selectedIndex);
    };

    const scrollSuggestionMenu = (index: number) => {
        if (!element) return;

        const suggestionElement = tagItemRefs[index];

        if (index === -1) {
            element.scrollTo({
                top: inverseList ? 0 : 100000,
                behavior: "smooth",
            });
            return;
        }

        if (suggestionElement && isElementOverflown(suggestionElement)) {
            suggestionElement.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
            });
        }
    };

    const scrollElementToPosition = (position: number) => {
        requestAnimationFrame(() => {
            if (!element) return;

            element.scrollTo({
                top: isComment ? -position : position,
                behavior: "instant",
            });
        });
    };

    const onSelect = async (idx: number) => {
        if (editingTagId) {
            stopEditingTag();
            return;
        }

        if (userRole < TopicAccessLevel.COMMENTER) {
            return;
        }
        const item = menus[idx];
        if (item) {
            props.command(item);
        } else {
            let newTag = {
                id: uuidv4(),
                tag: props.query.toLowerCase(),
                tenantId: $TenantStore?.active?.id,
            };
            props.command(newTag);
            ActiveTopicStore.createTag(newTag, $TenantStore?.active?.id);
        }
    };

    const startEditingTag = (tagId: uuidv4) => {
        if (editingTagId && editingTagId !== tagId) {
            stopEditingTag();
        } else {
            editingTagId = tagId;
        }
    };

    const stopEditingTag = () => {
        editingTagId = null;
    };
</script>

<div
    class="suggestion-wrapper"
    id="suggestionList"
    class:suggestion-wrapper--tags={!isComment}
    use:clickOutside={{
        ignoreSelectors: [".rename-input"],
    }}
    on:click_outside={stopEditingTag}
>
    {#if menus.length === 0 && props.query}
        {#if userRole >= TopicAccessLevel.EDITOR}
            <button
                class="create-tag-button active"
                on:click={() => onSelect(0)}
            >
                <p class="text">
                    Create tag <b>#{props.query.toLowerCase()}</b>
                </p>
            </button>
        {:else}
            <div class="no-tags">
                <p>No tags found</p>
            </div>
        {/if}
    {:else if menus.length > 0}
        <div
            class="results"
            style="max-height:{SCROLL_HEIGHT}px"
            bind:this={element}
        >
            {#if props.query && isNewTagButtonVisible && userRole >= 3}
                <button
                    class="create-tag-button"
                    class:active={selectedIndex === -1}
                    on:click={() => onSelect(props.query.toLowerCase())}
                >
                    <p class="text">
                        Create tag <b>#{props.query.toLowerCase()}</b>
                    </p>
                </button>
            {/if}

            <div class="flex flex-col" class:flex-col-reverse={snippetTag}>
                {#each menus as item, index}
                    <TagItem
                        tagName={item.tag}
                        tagId={item.id}
                        active={selectedIndex === index}
                        onClick={() => onSelect(index)}
                        {editingTagId}
                        onStartEditing={() => startEditingTag(item.id)}
                        onStopEditing={stopEditingTag}
                        isEditable={!isComment}
                        bind:element={tagItemRefs[index]}
                    />
                {/each}
            </div>
        </div>
    {:else}
        <div class="no-tags">
            <p>No tags found</p>
        </div>
    {/if}
</div>

<style>
    .suggestion-wrapper {
        @apply relative my-1 box-border flex h-auto flex-row rounded-md border border-solid border-gray-200 bg-white p-1 font-sans shadow !important;
    }

    .create-tag-button {
        @apply block h-6 w-full cursor-pointer items-center rounded border-none bg-white p-0 outline-none hover:bg-gray-100 !important;
    }

    .create-tag-button .text {
        @apply m-0 truncate rounded px-2 text-left text-sm leading-6 text-black !important;
    }

    .create-tag-button.active {
        @apply bg-gray-100 !important;
    }

    .no-tags {
        @apply flex h-full w-full items-center justify-center !important;
    }

    .no-tags p {
        @apply text-sm text-gray-500 !important;
    }

    .results {
        @apply flex h-full w-full flex-col overflow-y-auto !important;
    }

    .results::-webkit-scrollbar {
        @apply h-3 w-3 bg-gray-100 !important;
    }

    .results::-webkit-scrollbar-thumb {
        @apply border-0 border-l border-solid border-gray-100 bg-gray-200 hover:bg-gray-300 !important;
    }

    .results::-webkit-scrollbar-thumb {
        @apply rounded-full border-l-2 border-solid border-transparent bg-clip-content !important;
    }

    .suggestion-wrapper--tags {
        @apply relative left-0 top-0 z-50 mx-2 h-fit w-[calc(100%-1rem)] !important;
    }
</style>
